var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import Modal from '../../ui/modal';
import { validateWidget } from '../network';
import { WIDGET_TYPES, CHART_TYPES } from '../constants';
import BuilderMode from './BuilderMode';
import SqlMode from './SqlMode';
import HistoryTab from './HistoryTab';
const WidgetEditor = ({ isOpen, onClose, onSave, tables, editWidget }) => {
    var _a;
    const [name, setName] = React.useState('');
    const [widgetType, setWidgetType] = React.useState('bar');
    const [tab, setTab] = React.useState('builder');
    // Data Set: table dropdown (locks when SQL tab has a query)
    const [selectedTable, setSelectedTable] = React.useState('');
    // Custom query columns (populated when SQL query is validated for builder use)
    const [customQueryColumns, setCustomQueryColumns] = React.useState([]);
    const [applyingColumns, setApplyingColumns] = React.useState(false);
    // Builder state
    const [selectedColumns, setSelectedColumns] = React.useState([]);
    const [aggregation, setAggregation] = React.useState('');
    const [aggregationColumn, setAggregationColumn] = React.useState('');
    const [groupBy, setGroupBy] = React.useState([]);
    const [orderBy, setOrderBy] = React.useState('');
    const [limit, setLimit] = React.useState(null);
    // Field-well state (chart types)
    const [xAxisField, setXAxisField] = React.useState(null);
    const [yAxisField, setYAxisField] = React.useState(null);
    const [yAxisAgg, setYAxisAgg] = React.useState('count');
    const [colorField, setColorField] = React.useState(null);
    // SQL tab state
    const [sqlQuery, setSqlQuery] = React.useState('');
    const [sqlParams, setSqlParams] = React.useState('{}');
    const sqlEditorRef = React.useRef(null);
    // Chart config
    const [colors, setColors] = React.useState('#4F46E5');
    // Validation
    const [validationError, setValidationError] = React.useState(null);
    const [validating, setValidating] = React.useState(false);
    const isChartType = CHART_TYPES.has(widgetType);
    // Derived: SQL tab has a query → data set dropdown is locked
    const customQueryLocked = sqlQuery.trim() !== '';
    // Track which query columns were last loaded/attempted for
    const lastColumnLoadAttempt = React.useRef('');
    // Load columns for custom query — tries validate endpoint, falls back to widget execution
    const loadCustomQueryColumns = React.useCallback((query) => __awaiter(void 0, void 0, void 0, function* () {
        if (!query.trim())
            return;
        lastColumnLoadAttempt.current = query;
        setApplyingColumns(true);
        const setColumnsFromNames = (names) => {
            setCustomQueryColumns(names.map(c => ({ columnName: c, dataType: 'text', isNullable: true })));
        };
        // Try 1: validate endpoint returns column names
        try {
            const cqConfig = JSON.stringify({ mode: 'sql', query });
            const result = yield validateWidget(cqConfig);
            if (result.valid && result.columns && result.columns.length > 0) {
                setColumnsFromNames(result.columns);
                setApplyingColumns(false);
                return;
            }
        }
        catch ( /* continue to fallback */_a) { /* continue to fallback */ }
        // Try 2: for saved widgets, get columns from actual execution data (bypasses withDone)
        if (editWidget === null || editWidget === void 0 ? void 0 : editWidget.id) {
            try {
                const res = yield fetch(`/api/analytics/widgets/${editWidget.id}/exec`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('userToken') || ''}` },
                });
                if (res.ok) {
                    const data = yield res.json();
                    if (data && data.length > 0) {
                        setColumnsFromNames(Object.keys(data[0]));
                        setApplyingColumns(false);
                        return;
                    }
                }
            }
            catch ( /* continue */_b) { /* continue */ }
        }
        setApplyingColumns(false);
    }), [editWidget]);
    // Auto-load columns when builder tab is active with a locked custom query
    React.useEffect(() => {
        if (tab === 'builder' && customQueryLocked && customQueryColumns.length === 0 && !applyingColumns && lastColumnLoadAttempt.current !== sqlQuery) {
            loadCustomQueryColumns(sqlQuery);
        }
    }, [tab, customQueryLocked, customQueryColumns.length, applyingColumns, sqlQuery, loadCustomQueryColumns]);
    // When switching to builder tab, just set the tab — the useEffect handles column loading
    const switchToBuilder = React.useCallback(() => {
        setTab('builder');
    }, []);
    // Clear the custom query lock
    const handleClearCustomQuery = () => {
        setSqlQuery('');
        setSqlParams('{}');
        setCustomQueryColumns([]);
        lastColumnLoadAttempt.current = '';
    };
    React.useEffect(() => {
        var _a;
        // Reset attempt tracking on open/close
        lastColumnLoadAttempt.current = '';
        if (editWidget) {
            setName(editWidget.name);
            setWidgetType(editWidget.widgetType);
            try {
                const qc = JSON.parse(editWidget.queryConfig);
                if (qc.mode === 'sql') {
                    setTab('sql');
                    setSqlQuery(qc.query || '');
                    setSqlParams(JSON.stringify(qc.parameters || {}));
                    setSelectedTable('');
                }
                else {
                    setTab('builder');
                    setSelectedColumns(qc.columns || []);
                    setAggregation(qc.aggregation || '');
                    setAggregationColumn(qc.aggregationColumn || '');
                    const gb = qc.groupBy;
                    const gbArr = Array.isArray(gb) ? gb : gb ? [gb] : [];
                    setGroupBy(gbArr);
                    setOrderBy(qc.orderBy || '');
                    setLimit((_a = qc.limit) !== null && _a !== void 0 ? _a : null);
                    setXAxisField(gbArr[0] || null);
                    setColorField(gbArr[1] || null);
                    setYAxisAgg(qc.aggregation || 'count');
                    setYAxisField(qc.aggregationColumn || null);
                    if (qc.customQuery) {
                        // Builder was using a custom query — restore it into the SQL tab
                        setSqlQuery(qc.customQuery);
                        setSelectedTable('');
                    }
                    else if (qc.tableName) {
                        setSelectedTable(qc.tableName);
                        setSqlQuery('');
                        setCustomQueryColumns([]);
                    }
                }
            }
            catch ( /* ignore */_b) { /* ignore */ }
            try {
                const cc = JSON.parse(editWidget.chartConfig);
                if (Array.isArray(cc.colors) && cc.colors.length > 0)
                    setColors(cc.colors[0]);
            }
            catch ( /* ignore */_c) { /* ignore */ }
        }
        else {
            setName('');
            setWidgetType('bar');
            setTab('builder');
            setSelectedTable('');
            setSqlQuery('');
            setSqlParams('{}');
            setCustomQueryColumns([]);
            setSelectedColumns([]);
            setAggregation('');
            setAggregationColumn('');
            setGroupBy([]);
            setOrderBy('');
            setLimit(null);
            setXAxisField(null);
            setYAxisField(null);
            setYAxisAgg('count');
            setColorField(null);
            setColors('#4F46E5');
        }
        setValidationError(null);
    }, [editWidget, isOpen, tables]);
    // Executable SQL — used for SQL tab auto-population when switching from builder
    const executableSql = React.useMemo(() => {
        const hasSource = customQueryLocked ? !!sqlQuery : !!selectedTable;
        if (!hasSource)
            return '';
        const fromClause = customQueryLocked ? `(${sqlQuery}) AS _src` : `"${selectedTable}"`;
        const parts = [];
        if (isChartType) {
            const gb = [xAxisField, colorField].filter((x) => !!x);
            if (yAxisAgg === 'count' || !yAxisField) {
                parts.push(gb.length > 0
                    ? `SELECT ${gb.map(c => `"${c}"`).join(', ')}, COUNT(*) as value`
                    : 'SELECT COUNT(*) as value');
            }
            else {
                const aggStr = `${yAxisAgg.toUpperCase()}("${yAxisField}")`;
                parts.push(gb.length > 0
                    ? `SELECT ${gb.map(c => `"${c}"`).join(', ')}, ${aggStr} as value`
                    : `SELECT ${aggStr} as value`);
            }
            parts.push(`FROM ${fromClause}`);
            if (gb.length > 0)
                parts.push(`GROUP BY ${gb.map(c => `"${c}"`).join(', ')}`);
        }
        else if (widgetType === 'metric') {
            if (aggregation && aggregationColumn && aggregation !== 'count') {
                parts.push(`SELECT ${aggregation.toUpperCase()}("${aggregationColumn}") as value`);
            }
            else {
                parts.push('SELECT COUNT(*) as value');
            }
            parts.push(`FROM ${fromClause}`);
        }
        else {
            if (aggregation && aggregationColumn && groupBy.length > 0) {
                parts.push(`SELECT ${groupBy.map(c => `"${c}"`).join(', ')}, ${aggregation.toUpperCase()}("${aggregationColumn}") as value`);
            }
            else if (aggregation && groupBy.length > 0) {
                parts.push(`SELECT ${groupBy.map(c => `"${c}"`).join(', ')}, COUNT(*) as value`);
            }
            else if (selectedColumns.length > 0) {
                parts.push(`SELECT ${selectedColumns.map(c => `"${c}"`).join(', ')}`);
            }
            else {
                parts.push('SELECT *');
            }
            parts.push(`FROM ${fromClause}`);
            if (groupBy.length > 0)
                parts.push(`GROUP BY ${groupBy.map(c => `"${c}"`).join(', ')}`);
        }
        if (orderBy)
            parts.push(`ORDER BY ${orderBy}`);
        if (limit)
            parts.push(`LIMIT ${limit}`);
        return parts.join('\n');
    }, [sqlQuery, customQueryLocked, selectedTable, selectedColumns, aggregation, aggregationColumn, groupBy, orderBy, limit, isChartType, xAxisField, yAxisField, yAxisAgg, colorField, widgetType]);
    const switchToSql = () => {
        if (tab === 'builder' && executableSql) {
            setSqlQuery(executableSql);
        }
        setTab('sql');
    };
    const handleTableChange = (table) => {
        setSelectedTable(table);
        // Reset builder fields when changing table
        setSelectedColumns([]);
        setGroupBy([]);
        setAggregationColumn('');
        setAggregation('');
        setOrderBy('');
        setXAxisField(null);
        setYAxisField(null);
        setYAxisAgg('count');
        setColorField(null);
    };
    const buildQueryConfig = () => {
        if (tab === 'sql') {
            let params = {};
            try {
                params = JSON.parse(sqlParams);
            }
            catch ( /* ignore */_a) { /* ignore */ }
            return { mode: 'sql', query: sqlQuery, parameters: params };
        }
        // Builder mode: use customQuery (from SQL tab) or tableName
        const base = { mode: 'builder' };
        if (customQueryLocked) {
            base.customQuery = sqlQuery;
        }
        else {
            base.tableName = selectedTable;
        }
        if (isChartType) {
            const gb = [xAxisField, colorField].filter((x) => !!x);
            return Object.assign(Object.assign({}, base), { aggregation: yAxisAgg || 'count', aggregationColumn: (yAxisAgg && yAxisAgg !== 'count') ? yAxisField || null : null, groupBy: gb.length > 0 ? gb : null, orderBy: orderBy || null, limit: limit || null });
        }
        return Object.assign(Object.assign({}, base), { columns: selectedColumns, aggregation: aggregation || null, aggregationColumn: aggregationColumn || null, groupBy: groupBy.length > 0 ? groupBy : null, orderBy: orderBy || null, limit: limit || null });
    };
    const buildChartConfig = () => {
        if (isChartType) {
            return JSON.stringify({ colors: [colors], xAxisColumn: xAxisField, yAxisColumn: 'value', colorColumn: colorField || undefined });
        }
        return JSON.stringify({ colors: [colors] });
    };
    const handleSave = () => __awaiter(void 0, void 0, void 0, function* () {
        const queryConfig = buildQueryConfig();
        const qcStr = JSON.stringify(queryConfig);
        const chartConfig = buildChartConfig();
        setValidating(true);
        setValidationError(null);
        try {
            const result = yield validateWidget(qcStr);
            if (!result.valid) {
                setValidationError(result.error || 'Query validation failed');
                setValidating(false);
                return;
            }
        }
        catch ( /* ignore */_a) { /* ignore */ }
        setValidating(false);
        onSave({ name: name || 'Untitled Widget', widgetType, queryConfig: qcStr, chartConfig });
    });
    const handleForceSave = () => {
        setValidationError(null);
        onSave({
            name: name || 'Untitled Widget',
            widgetType,
            queryConfig: JSON.stringify(buildQueryConfig()),
            chartConfig: buildChartConfig(),
        });
    };
    return (_jsxs(Modal, { isOpen: isOpen, title: editWidget ? 'Edit Widget' : 'New Widget', size: "xl", onClose: onClose, children: [_jsxs("div", { className: "mb-3", children: [_jsx("label", { className: "form-label small fw-medium", children: "Widget Name" }), _jsx("input", { type: "text", className: "form-control form-control-sm", value: name, onChange: e => setName(e.target.value), placeholder: "e.g. Revenue by Month" })] }), _jsxs("div", { className: "mb-2", children: [_jsx("label", { className: "form-label small fw-medium", children: "Chart Type" }), _jsx("div", { className: "d-flex flex-wrap gap-2", children: WIDGET_TYPES.map(wt => (_jsxs("button", { className: `btn btn-sm ${widgetType === wt.value ? 'btn-primary' : 'btn-outline-secondary'}`, onClick: () => setWidgetType(wt.value), children: [_jsx("i", { className: `bi ${wt.icon} me-1` }), wt.label] }, wt.value))) })] }), _jsxs("div", { className: "mb-2", children: [_jsx("label", { className: "form-label small fw-medium", children: "Data Set" }), customQueryLocked ? (_jsxs("div", { className: "d-flex align-items-center gap-2", children: [_jsxs("div", { className: "form-control form-control-sm bg-body-secondary d-flex align-items-center gap-2", style: { cursor: 'default' }, children: [_jsx("i", { className: "bi bi-lock-fill text-body-secondary", style: { fontSize: '0.7rem' } }), _jsx("span", { className: "small", children: "Custom Query" }), _jsx("span", { className: "badge bg-primary-subtle text-primary ms-1", style: { fontSize: '0.65rem' }, children: "from SQL tab" })] }), _jsxs("button", { className: "btn btn-sm btn-outline-secondary flex-shrink-0", onClick: handleClearCustomQuery, children: [_jsx("i", { className: "bi bi-x-lg me-1" }), "Clear"] })] })) : (_jsxs("select", { className: "form-select form-select-sm", value: selectedTable, onChange: e => handleTableChange(e.target.value), children: [_jsx("option", { value: "", children: "Select a table..." }), tables.map(t => _jsx("option", { value: t, children: t }, t))] }))] }), _jsxs("ul", { className: "nav nav-tabs mb-0", children: [_jsx("li", { className: "nav-item", children: _jsx("button", { className: `nav-link ${tab === 'builder' ? 'active' : ''}`, onClick: switchToBuilder, children: "Builder" }) }), _jsx("li", { className: "nav-item", children: _jsx("button", { className: `nav-link ${tab === 'sql' ? 'active' : ''}`, onClick: switchToSql, children: "SQL" }) }), _jsx("li", { className: "nav-item", children: _jsx("button", { className: `nav-link ${tab === 'history' ? 'active' : ''}`, onClick: () => setTab('history'), disabled: !editWidget, children: "History" }) })] }), tab === 'history' ? (_jsx(HistoryTab, { widgetId: (_a = editWidget === null || editWidget === void 0 ? void 0 : editWidget.id) !== null && _a !== void 0 ? _a : null, onRestore: () => { onClose(); /* parent will re-fetch */ } })) : tab === 'builder' ? (_jsx(BuilderMode, { selectedTable: selectedTable, tables: tables, widgetType: widgetType, isChartType: isChartType, selectedColumns: selectedColumns, setSelectedColumns: setSelectedColumns, aggregation: aggregation, setAggregation: setAggregation, aggregationColumn: aggregationColumn, setAggregationColumn: setAggregationColumn, groupBy: groupBy, setGroupBy: setGroupBy, orderBy: orderBy, setOrderBy: setOrderBy, limit: limit, setLimit: setLimit, xAxisField: xAxisField, setXAxisField: setXAxisField, yAxisField: yAxisField, setYAxisField: setYAxisField, yAxisAgg: yAxisAgg, setYAxisAgg: setYAxisAgg, colorField: colorField, setColorField: setColorField, colors: colors, setColors: setColors, customQueryColumns: customQueryLocked ? customQueryColumns : undefined, customQueryLoading: customQueryLocked && applyingColumns })) : (_jsx(SqlMode, { sqlQuery: sqlQuery, setSqlQuery: setSqlQuery, sqlParams: sqlParams, setSqlParams: setSqlParams, sqlEditorRef: sqlEditorRef, tables: tables })), applyingColumns && tab === 'builder' && (_jsxs("div", { className: "text-center py-2 small text-body-secondary", children: [_jsx("span", { className: "spinner-border spinner-border-sm me-1" }), "Loading columns from SQL query..."] })), validationError && (_jsxs("div", { className: "alert alert-warning d-flex align-items-start gap-2 mt-2 mb-0 py-2 small", role: "alert", children: [_jsx("i", { className: "bi bi-exclamation-triangle-fill mt-1" }), _jsxs("div", { className: "flex-grow-1", children: [_jsx("strong", { children: "Query validation failed:" }), " ", validationError, _jsx("div", { className: "mt-1", children: _jsx("button", { className: "btn btn-sm btn-outline-warning", onClick: handleForceSave, children: "Save Anyway" }) })] })] })), _jsxs("div", { className: "d-flex justify-content-end gap-2 mt-3", children: [_jsx("button", { className: "btn btn-sm btn-outline-secondary", onClick: onClose, children: "Cancel" }), _jsx("button", { className: "btn btn-sm btn-primary", onClick: handleSave, disabled: validating, children: validating ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "spinner-border spinner-border-sm me-1" }), " Validating..."] })) : (_jsxs(_Fragment, { children: [_jsx("i", { className: "bi bi-check-lg me-1" }), editWidget ? 'Update' : 'Create', " Widget"] })) })] })] }));
};
export default WidgetEditor;
